
import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { GuestRoute } from '@/components/auth/GuestRoute';
import React, { Suspense, lazy } from 'react';
import { ROUTES } from '@/config/routes';

// Route sul percorso critico / frequenti: restano nel bundle iniziale per
// evitare un flash di caricamento al primo paint.
import HomePage from '@/pages/HomePage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import PasswordResetSuccessPage from '@/pages/PasswordResetSuccessPage';
import ProfilePage from '@/pages/ProfilePage';
import ShoppingPage from '@/pages/ShoppingPage';
import SettingsPage from '@/pages/SettingsPage';
import ShoppingListsPage from '@/pages/ShoppingListsPage';
import NotFound from '@/pages/NotFound';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import VerifyBridge from '@/pages/VerifyBridge';
import LogoutPage from '@/pages/LogoutPage';

// Route rare/pesanti: caricate on-demand come chunk separati. In particolare
// ImportShopsPage trascina react-leaflet/leaflet, che cosi' escono dal main chunk.
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const ShoppingHistoryPage = lazy(() => import('@/pages/ShoppingHistoryPage'));
const SharedListsPage = lazy(() => import('@/pages/SharedListsPage'));
const PremiumPage = lazy(() => import('@/pages/PremiumPage'));
const PremiumSuccessPage = lazy(() => import('@/pages/PremiumSuccessPage'));
const ShopsPage = lazy(() => import('@/pages/ShopsPage'));
const ImportShopsPage = lazy(() => import('@/pages/ImportShopsPage'));
const RecurringProductsPage = lazy(() => import('@/pages/RecurringProductsPage'));
const DevicesPage = lazy(() => import('@/pages/DevicesPage'));
const PrivacyPolicy = lazy(() => import('@/pages/privacy-policy'));
const DataDeletion = lazy(() => import('@/pages/data-deletion'));
const LicensePage = lazy(() => import('@/pages/license'));
const OfflineConfigPage = lazy(() => import('@/pages/OfflineConfigPage'));

interface AppRoutesProps {
  loginComponent: React.ReactNode;
}

// Fallback leggero mostrato mentre il chunk della route viene scaricato:
// mai pagina bianca, accessibile e in italiano.
const RouteLoadingFallback = () => (
  <div
    className="flex min-h-[50vh] w-full items-center justify-center"
    role="status"
    aria-live="polite"
    aria-label="Caricamento della pagina in corso"
  >
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-spesa-green-500 border-t-transparent" />
    <span className="sr-only">Caricamento…</span>
  </div>
);

export const AppRoutes = ({ loginComponent }: AppRoutesProps) => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <GuestRoute>
          {loginComponent}
        </GuestRoute>
      } />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/password-reset-success" element={<PasswordResetSuccessPage />} />
      <Route path={ROUTES.VERIFY_EMAIL.path} element={<VerifyEmailPage />} />
      <Route path="/verify-bridge" element={<VerifyBridge />} />
      <Route path="/logout" element={<LogoutPage />} />

      {/* Redirect root to home for authenticated users or login for guests */}
      <Route path="/" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      
      {/* Esplicitamente aggiunto anche il percorso /home che punta alla stessa HomePage */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/shopping" element={
        <ProtectedRoute>
          <ShoppingPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/offline-config" element={
        <ProtectedRoute>
          <OfflineConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/lists" element={
        <ProtectedRoute>
          <ShoppingListsPage />
        </ProtectedRoute>
      } />
      <Route path="/categories" element={
        <ProtectedRoute>
          <CategoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute>
          <ProductsPage />
        </ProtectedRoute>
      } />
      <Route path="/shopping-history" element={
        <ProtectedRoute>
          <ShoppingHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/shared-lists" element={
        <ProtectedRoute>
          <SharedListsPage />
        </ProtectedRoute>
      } />
      <Route path="/premium" element={
        <ProtectedRoute>
          <PremiumPage />
        </ProtectedRoute>
      } />
      <Route path="/premium-success" element={
        <ProtectedRoute>
          <PremiumSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/shops" element={
        <ProtectedRoute>
          <ShopsPage />
        </ProtectedRoute>
      } />
      <Route path="/import-shops" element={
        <ProtectedRoute>
          <ImportShopsPage />
        </ProtectedRoute>
      } />
      <Route path="/devices" element={
        <ProtectedRoute>
          <DevicesPage />
        </ProtectedRoute>
      } />
      <Route path="/recurring-products" element={
        <ProtectedRoute>
          <RecurringProductsPage />
        </ProtectedRoute>
      } />
      <Route path="/license" element={<LicensePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};
